import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import LoadingSkeletonProductList from '@/components/product/LoadingSkeletonProductList';
import ProductList from '@/components/product/ProductList';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { INotifications } from '@/interfaces/api/notifications/INotifications';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
// import { FilterStateSideBar } from '@/interfaces/home/IFilterStateSideBar';
import { ProductCardType } from '@/interfaces/notifications/Notifications';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

// interface OutletContext {
// 	filters: FilterStateSideBar;
// }

const Home = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchParams] = useSearchParams();
	// const { filters } = useOutletContext<OutletContext>();

	const {
		data: products,
		isLoading: productsIsLoading,
		isError: productIsError,
		error: productError,
	} = useQuery<ICollectionProduct>({
		queryKey: [
			ApiQueryKeys.getAllHaggleProducts,
			pageSize,
			pageNumber,
			searchParams.toString(),
		],
		queryFn: () =>
			haggleApiService.getAllHaggleProducts({
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
					'filterQuery[vendor]': searchParams.getAll('vendor'),
					'filterQuery[productType]': searchParams.getAll('type'),
					'filterQuery[countryName]': searchParams.getAll('country'),
					'filterQuery[minPrice]': searchParams.get('minPrice')
						? Number(searchParams.get('minPrice'))
						: undefined,
					'filterQuery[maxPrice]': searchParams.get('maxPrice')
						? Number(searchParams.get('maxPrice'))
						: undefined,
					'filterQuery[status]': searchParams.get('status') || undefined,
					'filterQuery[showPublicEventsOnly]': 'b2c',
				},
			}),
		refetchOnWindowFocus: false,
	});

	const { user } = useUser();

	const {
		data: favouriteNotifications,
		isSuccess: isFavouritesLoaded,
		refetch: refetchFavourites,
	} = useQuery<INotifications[]>({
		queryKey: [ApiQueryKeys.getFavouritedNotifications],
		queryFn: () => haggleApiService.getFavouritedNotifications(),
		enabled: !!user,
		refetchOnWindowFocus: false,
	});

	if (productIsError) {
		if (productError instanceof Error) {
			notificationService.error(productError.message);
		} else {
			notificationService.error('Unknown error fetching products');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionProduct,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	return (
		<div className="flex flex-col">
			<div className="flex-1">
				{productsIsLoading && <LoadingSkeletonProductList />}
				{/* Always render ProductList - it will use mock data when API fails */}
				{products && !productsIsLoading && (
					<ProductList
						type={ProductCardType.Home}
						collectionProduct={products}
						favouriteMerchants={
							isFavouritesLoaded ? favouriteNotifications : []
						}
						refetchFavourites={refetchFavourites}
						paginationOption={handlePaginationOptions(products)}
						// filters={filters}
					/>
				)}
			</div>
		</div>
	);
};

export default Home;
